import React, { useState } from 'react';
import * as translator from '../../../../utils/translator';
import './PhotoUploadModal.css';
import './UkVerificationTasks.css';
import { clientConfig } from '../../../../config';
import dontIcon from './icons/photo-dont.svg';

const silhouetteFront = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-silhouette-front.png`;
const silhouetteSide = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-silhouette-side.png`;
const mirrorIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-mirror.svg`;
const fullIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-full.svg`;
const starsIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-stars.svg`;
const userIcon = `${clientConfig.IMAGES_URL}uploads/icons/photo-upload-user.svg`;

interface Props {
  language: string;
  stepLabel: string;
  onClose: () => void;
  onContinue: () => void;
}

/** UK WL task flow (HL-12997): the photo instructions screen shown before the upload (AC-8). */
const PhotoInstructionsModal: React.FC<Props> = ({ language, stepLabel, onClose, onContinue }) => {
  const t = (key: string) => translator.translate(language, 'my_account', `VerificationTasks/${key}`);
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  return (
    <div
      className={`pum-overlay${visible ? ' pum-overlay-visible' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`pum-sheet vt-sheet-wide${visible ? ' pum-sheet-visible' : ''}`}>
        <div className="pum-header vt-header">
          <button className="vt-mobile-back" onClick={handleClose}>{t('mobile_back')}</button>
          <div className="pum-breadcrumb">
            <span className="pum-step vt-step-label">{stepLabel}</span>
            <span className="pum-breadcrumb-sep"> / </span>
            <span className="pum-breadcrumb-section vt-section-label">{t('photo_section_label')}</span>
          </div>
          <button className="pum-close-btn" onClick={handleClose} aria-label={t('close_aria')}>✕</button>
        </div>

        <div className="pum-body">
          <div className="pum-section-card pum-info-card">
            <div className="pum-info-text">
              <p className="pum-info-title">{t('photo_guide_title')}</p>
              <p className="pum-info-desc">{t('photo_guide_desc')}</p>
              <p className="vtg-hint vtg-hint-desktop">{t('photo_guide_hint')}</p>
            </div>
            <div className="pum-silhouettes-row">
              <div className="pum-silhouette-card">
                <div className="pum-silhouette-img-wrap">
                  <img src={silhouetteFront} alt="" className="pum-silhouette-img" />
                </div>
                <p className="pum-silhouette-label">{t('photo_guide_front')}</p>
              </div>
              <div className="pum-silhouette-card">
                <div className="pum-silhouette-img-wrap">
                  <img src={silhouetteSide} alt="" className="pum-silhouette-img" />
                </div>
                <p className="pum-silhouette-label">{t('photo_guide_side')}</p>
              </div>
            </div>
            <p className="vtg-hint vtg-hint-mobile">{t('photo_guide_hint')}</p>
          </div>

          <div className="pum-section-card pum-rec-card">
            <p className="pum-rec-title">{t('photo_guide_rec_title')}</p>
            <div className="pum-rec-grid">
              <div className="pum-rec-row">
                <div className="pum-guide-chip">
                  <div className="pum-guide-icon-lg">
                    <img src={mirrorIcon} alt="" />
                  </div>
                  <span className="pum-guide-text">{t('photo_guide_rec_mirror')}</span>
                </div>
                <div className="pum-guide-chip">
                  <div className="pum-guide-icon">
                    <img src={fullIcon} alt="" />
                  </div>
                  <span className="pum-guide-text">{t('photo_guide_rec_full')}</span>
                </div>
              </div>
              <div className="pum-rec-row">
                <div className="pum-guide-chip">
                  <div className="pum-guide-icon-lg">
                    <img src={starsIcon} alt="" />
                  </div>
                  <span className="pum-guide-text">{t('photo_guide_rec_lit')}</span>
                </div>
                <div className="pum-guide-chip">
                  <div className="pum-guide-icon">
                    <img src={userIcon} alt="" />
                  </div>
                  <span className="pum-guide-text">{t('photo_guide_rec_solo')}</span>
                </div>
              </div>
            </div>
            <div className="vtg-donts">
              <div className="vtg-dont">
                <img src={dontIcon} alt="" className="vtg-dont-icon" />
                <span className="vtg-dont-text">{t('photo_guide_dont_blur')}</span>
              </div>
              <div className="vtg-dont">
                <img src={dontIcon} alt="" className="vtg-dont-icon" />
                <span className="vtg-dont-text">{t('photo_guide_dont_filter')}</span>
              </div>
            </div>
          </div>

          <button className="pum-submit-btn pum-submit-btn-active" onClick={onContinue}>
            {t('photo_guide_continue')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoInstructionsModal;
